import {theme} from '@styles/theme';
import React, {ChangeEvent, FC, useEffect, useRef, useState} from 'react';
import {
  ErrorText,
  Label,
  Option,
  OptionTextWrapper,
  SearchingInput,
  SelectList,
  SelectWrapper,
  SearchingWrapper,
} from './Select.styles';
import Avatar from '@atoms/Avatar';
import Text from '@atoms/Text';
import Checkbox from '@atoms/Checkbox';

interface MultiSelectProps {
  id: string;
  value: string[];
  onChange?: (values: string[]) => void;
  options: Array<{title: string; value: string; avatar?: string; subtitle?: string}>;
  label?: string;
  style?: Record<string, any>;
  error?: string;
  inputDisabled?: boolean;
  disabled?: string;
  labelBG?: string;
  borderColor?: string;
  labelColor?: string;
  className?: string;
  rounded?: boolean;
  menuWidth?: string;
  wrap?: boolean;
  optimizeRendering?: boolean;
  placeholder?: string;
}

const MultiSelect: FC<MultiSelectProps> = ({
  id,
  value = [],
  onChange,
  options,
  label,
  style,
  error,
  inputDisabled,
  disabled,
  labelBG,
  borderColor,
  labelColor,
  className,
  menuWidth = 'inherit',
  rounded,
  wrap,
  optimizeRendering,
}) => {
  const [isFocus, setIsFocus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(value);
  const [listPosition, setListPosition] = useState<'top' | 'bottom'>('top');
  const [searchingValue, setSearchingValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selectListRef = useRef<HTMLDivElement>(null);
  const selectWrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSelectedValues(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectWrapperRef.current &&
        !selectWrapperRef.current.contains(event.target as Node) &&
        selectListRef.current &&
        !selectListRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsFocus(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleSelect = (optionValue: string, e: React.SyntheticEvent) => {
    e.stopPropagation();
    const newSelectedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];

    setSelectedValues(newSelectedValues);
    onChange?.(newSelectedValues);
  };

  const filteredOptions = options.filter(option => {
    return (
      String(option.value).toLowerCase().includes(searchingValue.toLowerCase()) ||
      String(option.title).toLowerCase().includes(searchingValue.toLowerCase())
    );
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => (prev === 0 ? filteredOptions.length - 1 : prev - 1));
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev => (prev === filteredOptions.length - 1 ? 0 : prev + 1));
    } else if (event.key === 'Enter' && filteredOptions[highlightedIndex]) {
      event.preventDefault();
      const newSelectedValues = selectedValues.includes(
        filteredOptions[highlightedIndex].value,
      )
        ? selectedValues.filter(v => v !== filteredOptions[highlightedIndex].value)
        : [...selectedValues, filteredOptions[highlightedIndex].value];
      setSelectedValues(newSelectedValues);
      if (onChange) onChange(newSelectedValues);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const searchCurrentValue = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchingValue(e.target.value);
    setHighlightedIndex(0);
  };

  const adjustDropdownPosition = () => {
    const rect = selectWrapperRef?.current?.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect!.bottom;
    setListPosition(spaceBelow < 300 ? 'bottom' : 'top');
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!inputDisabled) {
      adjustDropdownPosition();
      setIsOpen(!isOpen);
      setIsFocus(!isFocus);
    }
  };

  return (
    <>
      <SelectWrapper
        className={className}
        onClick={toggleDropdown}
        focus={isFocus}
        error={error}
        style={style}
        labelBG={labelBG}
        borderColor={borderColor}
        labelColor={labelColor}
        inputDisabled={inputDisabled}
        rounded={rounded}
        id={id}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        ref={selectWrapperRef}>
        <Label
          htmlFor={id}
          focus={isFocus}
          error={error}
          labelBG={labelBG}
          labelColor={labelColor}
          shrink={isFocus || !!searchingValue}>
          {label}
        </Label>

        <SearchingWrapper>
          <SearchingInput
            ref={searchInputRef}
            onClick={e => {
              e.stopPropagation();
              if (!isOpen) {
                adjustDropdownPosition();
                setIsOpen(true);
              }
            }}
            value={searchingValue}
            onChange={searchCurrentValue}
            onFocus={() => {
              adjustDropdownPosition();
              setIsOpen(true);
              setIsFocus(true);
            }}
            wrap={wrap}
            readOnly={!isOpen}
          />
        </SearchingWrapper>

        {(!optimizeRendering || isOpen) && (
          <SelectList
            isOpen={isOpen}
            id="selectList"
            ref={selectListRef}
            position={listPosition}
            menuWidth={menuWidth}>
            {filteredOptions.map((option, index) => (
              <Option
                key={`${option.value}-${index}`}
                onClick={e => handleToggleSelect(option.value, e)}
                onMouseMove={() => setHighlightedIndex(index)}
                className={index === highlightedIndex ? 'active' : ''}
                highlighted={index === highlightedIndex}
                choosed={selectedValues.includes(option.value)}
                disabled={option.value === disabled}>
                <Checkbox
                  id={`access-user-${option.value}`}
                  checked={selectedValues.includes(option.value)}
                  onChange={e => handleToggleSelect(option.value, e)}
                />
                <Avatar img={option.avatar} />
                <OptionTextWrapper>
                  <Text text={option.title} type="tinyM" color={theme.colors.basic01} />
                  {!!option.subtitle && (
                    <Text
                      text={option.subtitle}
                      type="tinyR"
                      color={theme.colors.basic02}
                    />
                  )}
                </OptionTextWrapper>
              </Option>
            ))}
          </SelectList>
        )}
      </SelectWrapper>
      {error && <ErrorText>{error}</ErrorText>}
    </>
  );
};

export default MultiSelect;
